import React, { useState, useCallback, useEffect } from "react";
import Modal from "react-modal";
import { Container, Row, Col } from "react-grid-system";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { Button } from "../Button";
import DocumentDropzone from "./DocumentDropzone";
import DocumentPreview from "./DocumentPreview";
import {
	StyledDocumentUploadIcon,
	StyledDocumentUploadOptions,
	StyledDocumentUploadActions,
} from "../../styles/StyledDocumentUpload";
import { StyledTallModal } from "../../styles/StyledModal/StyledModal";
import { StyledModalHeader } from "../../styles/StyledModalHeader/StyledModalHeader";
import UploadIcon from "../../assets/img/icons/upload.svg";
import PdfIcon from "../../assets/img/icons/pdf.svg";
import DeleteIcon from "../../assets/img/icons/delete.svg";

import { TextractClient, AnalyzeDocumentCommand, DetectDocumentTextCommand } from "@aws-sdk/client-textract";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import * as pdfjsLib from "pdfjs-dist/webpack";
import { uploadTempFile, removeTempFile } from "../../api/tempFile";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const UPLOAD_STEPS = {
	SELECT: "select",
	PROCESSING: "processing"
};

const UPLOAD_CONFIG = {
	maxFileSize: parseInt(process.env.REACT_APP_TEXTRACT_MAX_FILE_SIZE) || 5 * 1024 * 1024, // 5MB (conservative limit for Textract)
	allowedTypes: process.env.REACT_APP_TEXTRACT_ALLOWED_TYPES?.split(",") || [
		"application/pdf",
		"image/jpeg",
		"image/png",
	],
	maxFiles: 1, // Only one document at a time
	// Textract supported formats: PDF, PNG, JPEG, TIFF (but we limit to most common)
	textractLimits: {
		maxSyncSize: 10 * 1024 * 1024, // 10MB for synchronous processing
		recommendedSize: 2 * 1024 * 1024 // 2MB for best performance
	}
};

// Configure AWS clients using environment variables
const AWS_REGION = process.env.REACT_APP_AWS_REGION || "eu-west-1";
const COGNITO_IDENTITY_POOL_ID = process.env.REACT_APP_COGNITO_IDENTITY_POOL_ID || "eu-west-1:be2c3303-6d79-4300-994f-9242914b12b8";

const textractClient = new TextractClient({
  region: AWS_REGION,
  credentials: fromCognitoIdentityPool({
    clientConfig: { region: AWS_REGION },
    identityPoolId: COGNITO_IDENTITY_POOL_ID,
  }),
});

export default function DocumentUploadModal({ isOpen, onClose, onDecisionCreated }) {
	const navigate = useNavigate();
	const { axiosPrivate } = useAxiosPrivate();
	const [currentStep, setCurrentStep] = useState(UPLOAD_STEPS.SELECT);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [tempFileIds, setTempFileIds] = useState([]); // Store temporary file IDs
	const [uploadProgress, setUploadProgress] = useState({});
	const [errors, setErrors] = useState([]);
	const [isProcessing, setIsProcessing] = useState(false);
	const [processingStatus, setProcessingStatus] = useState("");

	// PDF to Image conversion function
	const convertPdfToImage = useCallback(async (pdfFile, setProcessingStatus) => {
		setProcessingStatus("Converting PDF to image format...");
		
		try {
			const arrayBuffer = await pdfFile.arrayBuffer();
			
			// Load the PDF document
			const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
			
			// For now, convert only the first page (can be extended for multi-page)
			const page = await pdf.getPage(1);
			const viewport = page.getViewport({ scale: 2.0 }); // Higher scale = better quality
			
			// Create canvas for rendering
			const canvas = document.createElement("canvas");
			const context = canvas.getContext("2d");
			canvas.height = viewport.height;
			canvas.width = viewport.width;
			
			// Render PDF page to canvas
			await page.render({
				canvasContext: context,
				viewport: viewport
			}).promise;
			
			// Convert canvas to blob
			const blob = await new Promise(resolve => {
				canvas.toBlob(resolve, "image/png", 0.95);
			});
			
			setProcessingStatus(`PDF converted to PNG (${Math.round(blob.size / 1024)}KB)`);
			
			return blob;
			
		} catch (error) {
			throw new Error(`PDF conversion failed: ${error.message}`);
		}
	}, []);



	useEffect(() => {
		if (isOpen) {
			// Reset state when modal opens
		}
	}, [isOpen]);

	const handleClose = useCallback(async () => {
		console.log("🔍 DocumentUpload: handleClose called, cleaning up temp files:", tempFileIds.length);
		
		// Cleanup temporary files on close (unless decision creation is in progress)
		if (tempFileIds.length > 0 && !isProcessing) {
			try {
				for (const tempFileId of tempFileIds) {
					await removeTempFile(axiosPrivate, tempFileId);
					console.log("🗑️ Cleaned up temp file:", tempFileId);
				}
			} catch (error) {
				console.warn("⚠️ Failed to cleanup some temp files:", error);
			}
		}
		
		setCurrentStep(UPLOAD_STEPS.SELECT);
		setSelectedFiles([]);
		setTempFileIds([]);
		setUploadProgress({});
		setErrors([]);
		setIsProcessing(false);
		setProcessingStatus("");
		
		onClose();
	}, [onClose, tempFileIds, isProcessing, axiosPrivate]);

	const validateFile = useCallback(async (file) => {
		const errors = [];

		// Check file size (5MB limit for optimal processing)
		if (file.size > UPLOAD_CONFIG.maxFileSize) {
			errors.push(`File "${file.name}" is too large. Maximum size is 5MB for optimal processing.`);
		}

		// Check if file is empty
		if (file.size === 0) {
			errors.push(`File "${file.name}" appears to be empty.`);
		}

		// Check file type
		if (!UPLOAD_CONFIG.allowedTypes.includes(file.type)) {
			errors.push(`File "${file.name}" has an unsupported format. Please use PDF, PNG, or JPEG files only.`);
		}

		// Additional PDF-specific validation
		if (file.type === "application/pdf") {
			try {
				const arrayBuffer = await file.arrayBuffer();
				const bytes = new Uint8Array(arrayBuffer);
				
				// Check PDF header
				const pdfHeader = new TextDecoder().decode(bytes.slice(0, 8));
				
				if (!pdfHeader.startsWith("%PDF-")) {
					errors.push(`File "${file.name}" does not appear to be a valid PDF file.`);
				}
				
				// Check for encryption markers (basic check)
				const pdfContent = new TextDecoder("latin1").decode(bytes);
				if (pdfContent.includes("/Encrypt") || pdfContent.includes("/Filter/Standard")) {
					errors.push(`PDF "${file.name}" appears to be password-protected or encrypted. Textract cannot process encrypted PDFs.`);
				}
				
				// Check file size warning
				if (file.size > 2 * 1024 * 1024) {
					errors.push(`PDF "${file.name}" is quite large (${Math.round(file.size / (1024 * 1024))}MB). Large PDFs may have processing issues.`);
				}
				
			} catch (validationError) {
				console.warn("Could not validate PDF format:", validationError);
				errors.push(`Could not validate PDF format for "${file.name}". File may be corrupted.`);
			}
		}

		return errors;
	}, []);

	const handleFilesSelected = useCallback(async (files) => {
		const fileArray = Array.from(files);
		// Check if multiple files are being uploaded
		if (fileArray.length > 1) {
			setErrors(["Please upload only one document at a time. Each document will create one decision."]);
			toast.warning("Please upload only one document at a time", { autoClose: 4000 });
			return;
		}

		// For single file upload, only process the first file
		const fileToProcess = fileArray[0];
		if (!fileToProcess) {
			return;
		}

		// Validate the file (now async)
		const fileErrors = await validateFile(fileToProcess);
		if (fileErrors.length === 0) {
			setErrors([]);
			setProcessingStatus("Uploading file to server...");
			setIsProcessing(true);

			try {
				// Upload file immediately to backend
				console.log("🔧 Uploading file to backend:", fileToProcess.name);
				const tempFileResponse = await uploadTempFile(axiosPrivate, fileToProcess);
				
				console.log("✅ Temp file uploaded successfully:", tempFileResponse);
				
				const validFile = {
					id: Date.now() + Math.random(),
					file: fileToProcess,
					name: fileToProcess.name,
					size: fileToProcess.size,
					type: fileToProcess.type,
					uploaded: true,
					tempFileId: tempFileResponse.id, // Store the backend temp file ID
					tempFileData: tempFileResponse // Store full response for later use
				};
				
				// Store temp file ID for cleanup
				setTempFileIds(prev => [...prev, tempFileResponse.id]);
				
				// Replace any existing file with the new one
				setSelectedFiles([validFile]);

				// Immediately start processing - no preview step
				setTimeout(() => {
					setCurrentStep(UPLOAD_STEPS.PROCESSING);
					handleProceedToConvert(validFile);
				}, 300);
				
			} catch (uploadError) {
				console.error("❌ Failed to upload file:", uploadError);
				setErrors([`Failed to upload file: ${uploadError.message}`]);
				setIsProcessing(false);
				toast.error("Failed to upload file to server", { autoClose: 8000 });
			}
		} else {
			setErrors(fileErrors);
			setSelectedFiles([]);
			toast.error(`File validation failed: ${fileErrors[0]}`, { autoClose: 8000 });
		}
	}, [validateFile, axiosPrivate]);

	const handleFileRemove = useCallback((fileId) => {
		setSelectedFiles(prev => prev.filter(f => f.id !== fileId));
		setErrors([]);
	}, []);

	// Textract processing with multiple strategies (NO FALLBACKS)
	const processDocumentWithTextract = async (file, setProcessingStatus) => {
		setProcessingStatus(`Starting AWS Textract analysis for ${file.name} (${Math.round(file.size / 1024)}KB)`);
		
		const arrayBuffer = await file.arrayBuffer();
		const bytes = new Uint8Array(arrayBuffer);
		
		// Strategy 1: Try AnalyzeDocument with advanced features
		try {
			setProcessingStatus("Premium Analysis - Extracting tables and forms...");
			
			const advancedCommand = new AnalyzeDocumentCommand({
				Document: { Bytes: bytes },
				FeatureTypes: ["TABLES", "FORMS"]
			});
			const response = await textractClient.send(advancedCommand);
			
			// Log what features Textract detected
			const blockTypes = [...new Set(response.Blocks.map(b => b.BlockType))];
			console.log("Textract Premium Analysis Block Types:", blockTypes);
			
			return { response, method: "Advanced Analysis", level: "Premium" };
		} catch (error) {
			console.log("Premium Analysis failed:", error.message);
			// Strategy failed, try next one
		}

		// Strategy 2: Try AnalyzeDocument without features (basic analysis)
		try {
			setProcessingStatus("Standard Analysis - Extracting text and layout...");
			
			const basicCommand = new AnalyzeDocumentCommand({
				Document: { Bytes: bytes }
			});
			const response = await textractClient.send(basicCommand);
			
			const blockTypes = [...new Set(response.Blocks.map(b => b.BlockType))];
			console.log("Textract Standard Analysis Block Types:", blockTypes);
			
			return { response, method: "Basic Analysis", level: "Standard" };
		} catch (error) {
			console.log("Standard Analysis failed:", error.message);
			// Strategy failed, try next one
		}

		// Strategy 3: Try DetectDocumentText (most basic)
		try {
			setProcessingStatus("Basic Detection - Extracting plain text...");
			
			const detectCommand = new DetectDocumentTextCommand({
				Document: { Bytes: bytes }
			});
			const response = await textractClient.send(detectCommand);
			
			const blockTypes = [...new Set(response.Blocks.map(b => b.BlockType))];
			console.log("Textract Basic Detection Block Types:", blockTypes);
			
			return { response, method: "Text Detection", level: "Basic" };
		} catch (error) {
			console.log("Basic Detection failed:", error.message);
			// All strategies failed - throw detailed error
			throw new Error(`All Textract strategies failed. Last error: ${error.name} - ${error.message}`);
		}
	};

	const handleProceedToConvert = useCallback(async (fileToProcess = null) => {
		const primaryFile = fileToProcess || selectedFiles[0];
		if (!primaryFile) return;

		// Clear any previous session data to prevent stale data issues
		sessionStorage.removeItem("prefillDecisionData");
		sessionStorage.removeItem("uploadedFileAttachment");
		// Note: Don't delete window.tempUploadedFile here as it will be set later in this function

		// Don't set these again if already set
		if (currentStep !== UPLOAD_STEPS.PROCESSING) {
			setCurrentStep(UPLOAD_STEPS.PROCESSING);
			setIsProcessing(true);
		}

		setProcessingStatus("Initializing document analysis...");
	  
		try {
		  // Validate file size for Textract (max 10MB for synchronous)
		  if (primaryFile.file.size > 10 * 1024 * 1024) {
			console.error("File too large:", primaryFile.file.size, "bytes");
			throw new Error("File too large for processing. Please use a file smaller than 5MB.");
		  }

		  // Validate that we have content
		  if (primaryFile.file.size === 0) {
			console.error("File is empty");
			throw new Error("File appears to be empty or corrupted.");
		  }

		  console.log("File validation passed");
		  setProcessingStatus("Connecting to AWS Textract...");
	  
		  // Prepare file for Textract processing
		  let fileToProcess = primaryFile.file;
		  let processingMethod = "Direct";
		  
		  // Convert PDF to image if it's a PDF (better compatibility)
		  if (primaryFile.file.type === "application/pdf") {
			try {
			  const imageBlob = await convertPdfToImage(primaryFile.file, setProcessingStatus);
			  fileToProcess = new File([imageBlob], primaryFile.name.replace(".pdf", ".png"), { 
				type: "image/png" 
			  });
			  processingMethod = "PDF-to-Image";			} catch (conversionError) {
			  // Continue with original PDF file
			}
		  }
	  
		  // Process document with Textract ONLY (no fallbacks)
		  const { response, method, level } = await processDocumentWithTextract(fileToProcess, setProcessingStatus);
		  
		  setProcessingStatus(`${level} analysis complete - Processing results...`);
	  
		  // Validate Textract response before processing
		  if (!response || !response.Blocks || !Array.isArray(response.Blocks)) {
			throw new Error(`Invalid Textract response: ${method} returned malformed data`);
		  }
		  
		  if (response.Blocks.length === 0) {
			console.warn(`${method} returned no text blocks - document may be empty or image-only`);
		  }

		  // Enhanced text extraction with better processing
		  let extractedText = "";
		  let extractedMetadata = { tables: [], forms: [], keyValuePairs: {} };

		  if (response.Blocks && Array.isArray(response.Blocks)) {
			// Extract text from LINE blocks
			const textBlocks = response.Blocks
			  .filter(block => block.BlockType === "LINE" && block.Text)
			  .map(block => block.Text.trim())
			  .filter(text => text.length > 0);

			extractedText = textBlocks.join(" ");

			// PROPERLY Extract Key-Value pairs using Textract's structured data
			const extractKeyValuePairs = (blocks) => {
			  const keyValuePairs = {};
			  const blockMap = {};
			  
			  // Create a map of all blocks for easy lookup
			  blocks.forEach(block => {
				blockMap[block.Id] = block;
			  });
			  
			  // Find KEY blocks and their corresponding VALUE blocks
			  const keyBlocks = blocks.filter(block => 
				block.BlockType === "KEY_VALUE_SET" && 
				block.EntityTypes && 
				block.EntityTypes.includes("KEY")
			  );
			  
			  keyBlocks.forEach(keyBlock => {
				let keyText = "";
				let valueText = "";
				
				// Extract key text
				if (keyBlock.Relationships) {
				  const childRelation = keyBlock.Relationships.find(rel => rel.Type === "CHILD");
				  if (childRelation && childRelation.Ids) {
					keyText = childRelation.Ids
					  .map(id => blockMap[id])
					  .filter(block => block && block.Text)
					  .map(block => block.Text)
					  .join(" ")
					  .trim();
				  }
				  
				  // Find the corresponding VALUE block
				  const valueRelation = keyBlock.Relationships.find(rel => rel.Type === "VALUE");
				  if (valueRelation && valueRelation.Ids) {
					const valueBlock = blockMap[valueRelation.Ids[0]];
					if (valueBlock && valueBlock.Relationships) {
					  const valueChildRelation = valueBlock.Relationships.find(rel => rel.Type === "CHILD");
					  if (valueChildRelation && valueChildRelation.Ids) {
						valueText = valueChildRelation.Ids
						  .map(id => blockMap[id])
						  .filter(block => block && block.Text)
						  .map(block => block.Text)
						  .join(" ")
						  .trim();
					  }
					}
				  }
				}
				
				if (keyText && valueText) {
				  keyValuePairs[keyText.toLowerCase()] = valueText;
				}
			  });
			  
			  return keyValuePairs;
			};
			
			// Extract structured key-value pairs
			extractedMetadata.keyValuePairs = extractKeyValuePairs(response.Blocks);
			
			// Comprehensive Textract Analysis Debug
			console.log("=== TEXTRACT ANALYSIS RESULTS ===");
			console.log("Method used:", method, "Level:", level);
			console.log("Total blocks found:", response.Blocks.length);
			console.log("Block types:", [...new Set(response.Blocks.map(b => b.BlockType))]);
			console.log("Key-Value Pairs Found:", extractedMetadata.keyValuePairs);
			console.log("Total Key-Value Pairs:", Object.keys(extractedMetadata.keyValuePairs).length);
			console.log("Tables found:", extractedMetadata.tables.length);
			console.log("Forms metadata:", extractedMetadata.forms.length);
			console.log("Extracted text length:", extractedText.length);
			console.log("First 200 chars of text:", extractedText.substring(0, 200));
			console.log("=== END TEXTRACT ANALYSIS ===");

			// Try to extract tables if available
			const tableBlocks = response.Blocks.filter(block => block.BlockType === "TABLE");
			if (tableBlocks.length > 0) {
			  extractedMetadata.tables = tableBlocks.map(table => ({
				id: table.Id,
				confidence: table.Confidence
			  }));
			}

			// Try to extract key-value pairs metadata 
			const keyBlocks = response.Blocks.filter(block => block.BlockType === "KEY_VALUE_SET");
			if (keyBlocks.length > 0) {
			  extractedMetadata.forms = keyBlocks.slice(0, 5).map(form => ({
				id: form.Id,
				confidence: form.Confidence
			  }));
			}
		  }

		  // Enhanced Swedish document field extraction using Textract + Regex fallback
		  const extractSwedishDocumentFields = (text, keyValuePairs = {}) => {
			
			const lines = text.split(/\r?\n/).map(line => line.trim()).filter(line => line.length > 0);
			const fullText = text.toLowerCase();
			
			console.log("Available Textract key-value pairs:", keyValuePairs);
			
			// Enhanced Swedish field extraction patterns
			const fieldPatterns = {
				title: [
					// Most specific pattern for GU documents - match exactly the format we see
					/STYRDOKUMENT\s+\d+\s+Dnr\s+[A-Z0-9/\s]+GÖTEBORGS\s+UNIVERSITET\s+(.+?)\s+Publicerad/i,
					// Alternative pattern if no "Publicerad" found
					/STYRDOKUMENT\s+\d+\s+Dnr\s+[A-Z0-9/\s]+GÖTEBORGS\s+UNIVERSITET\s+(.+?)(?:\s+http|$)/i,
					// Match specific Swedish document titles and stop at metadata - allow longer captures
					/^(handläggningsordning\s+för\s+[^P]+?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im,
					/^(policy\s+för\s+[^P]+?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im,
					/^(riktlinjer\s+för\s+[^P]+?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im,
					/^(strategier\s+och\s+rutiner\s+för\s+[^P]+?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im,
					// Enhanced generic pattern for documents, stop at metadata markers
					/^(.+?(?:ordning|policy|riktlinjer|strategier|rutiner|dokument|anvisningar|instruktioner)[^P]*?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im,
					// Pattern for disciplinary/complaint documents
					/^([\w\s]+?(?:anmälan|disciplin|förseelser|handläggning|rekrytering|kompetenförsörjning)[\w\s]*?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im,
					// Broader pattern for any meaningful title before metadata
					/^([A-ZÅÄÖ][^P]*?(?:för|och|av|inom|med|till)[^P]*?)(?:\s+Publicerad|Dnr|GU|\d{4}\/|$)/im
				],
				unit: [
					/(?:ansvarig\s+enhet):\s*(.+?)(?:\n|Beslutsdatum|$)/im,
					/(?:organisationsenhet):\s*(.+?)(?:\n|Beslutsdatum|$)/im,
					/(?:utfärdare):\s*(.+?)(?:\n|Beslutsdatum|$)/im,
					/(?:sektionen\s+för\s+(.+?))(?:\n|Beslutsdatum|$)/im,
					/(?:fakulteten\s+för\s+(.+?))(?:\n|Beslutsdatum|$)/im,
					/(?:institutionen\s+för\s+(.+?))(?:\n|Beslutsdatum|$)/im
				],
				decisionDate: [
					/(?:beslutsdatum|fastställd|antagen|godkänd):\s*(\d{4}-\d{2}-\d{2})$/im,
					/(?:beslutsdatum|fastställd|antagen|godkänd):\s*(\d{1,2}[/.-]\d{1,2}[/.-]\d{4})$/im,
					/(?:datum):\s*(\d{4}-\d{2}-\d{2})$/im,
					/(?:datum):\s*(\d{1,2}[/.-]\d{1,2}[/.-]\d{4})$/im
				],
				validityPeriod: [
					/(?:giltighetstid|gäller\s+från):\s*(.+?)(?:\n|Sammanfattning|$)/im,
					/(?:giltighet):\s*(.+?)(?:\n|Sammanfattning|$)/im
				],
				description: [
					/sammanfattning\s+(.+?)(?:\n\n|\n[A-ZÅÄÖ]|\nGemensamma|$)/im,
					/(?:syfte|innehåll):\s*(.+?)(?:\n\n|\n[A-ZÅÄÖ]|$)/im,
					/(?:detta\s+dokument\s+(.+?))(?:\n\n|\n[A-ZÅÄÖ]|$)/im
				],
				referenceNumber: [
					/(?:dnr|diarienummer):\s*(.+?)(?:\n|$)/im,
					/(?:gu\s*[-/]\s*\d+[-/]\d+)/im
				]
			};

			const extractedFields = {
				title: "",
				unit: "",
				decisionDate: "",
				validFrom: "",
				validTo: "",
				validityPeriod: "",
				description: "",
				referenceNumber: "",
				keywords: [],
				confidence: 0
			};

			// FIRST: Try to use Textract's structured key-value pairs
			const textractFieldMapping = {
				// Swedish field mappings to common Textract keys - expanded mapping
				"ansvarig enhet": "unit",
				"organisationsenhet": "unit", 
				"utfärdare": "unit",
				"enhet": "unit",
				"avdelning": "unit",
				"sektion": "unit",
				"fakultet": "unit",
				"institution": "unit",
				
				"beslutsdatum": "decisionDate",
				"fastställd": "decisionDate",
				"antagen": "decisionDate",
				"godkänd": "decisionDate",
				"datum": "decisionDate",
				
				"giltighetstid": "validityPeriod",
				"gäller från": "validFrom",
				"giltighet": "validityPeriod",
				"gäller till": "validTo",
				"gäller": "validityPeriod",
				
				"sammanfattning": "description",
				"syfte": "description", 
				"innehåll": "description",
				"beskrivning": "description",
				"bakgrund": "description",
				
				"dnr": "referenceNumber",
				"diarienummer": "referenceNumber",
				"ärendenummer": "referenceNumber",
				"ref": "referenceNumber"
			};
			
			console.log("=== TEXTRACT KEY-VALUE EXTRACTION DEBUG ===");
			console.log("Available Key-Value pairs:", keyValuePairs);
			console.log("Total pairs found:", Object.keys(keyValuePairs).length);
			
			// Extract from Textract key-value pairs first
			Object.entries(keyValuePairs).forEach(([key, value]) => {
				const normalizedKey = key.toLowerCase().trim();
				console.log(`🔍 Processing Textract key: "${normalizedKey}" = "${value}"`);
				
				// Direct mapping
				if (textractFieldMapping[normalizedKey]) {
					const targetField = textractFieldMapping[normalizedKey];
					if (!extractedFields[targetField] || extractedFields[targetField].length === 0) { // Don't overwrite if already found
						extractedFields[targetField] = value;
						console.log(`✅ Direct mapping: ${normalizedKey} → ${targetField}: "${value}"`);
					} else {
						console.log(`⚠️ Field ${targetField} already has value: "${extractedFields[targetField]}", skipping`);
					}
				}
				
				// Partial key matching for complex Swedish terms
				Object.entries(textractFieldMapping).forEach(([swedishKey, field]) => {
					if (normalizedKey.includes(swedishKey) && (!extractedFields[field] || extractedFields[field].length === 0)) {
						extractedFields[field] = value;
						console.log(`✅ Partial match: ${normalizedKey} contains ${swedishKey} → ${field}: "${value}"`);
					} else if (normalizedKey.includes(swedishKey)) {
						console.log(`⚠️ Partial match found but field ${field} already has value: "${extractedFields[field]}"`);
					}
				});
			});
			
			console.log("=== EXTRACTED FIELDS FROM TEXTRACT ===");
			Object.entries(extractedFields).forEach(([field, value]) => {
				if (value) {
					console.log(`${field}: "${value}"`);
				}
			});
			console.log("=========================================");

			// Extract title - prioritize document headers (AFTER Textract extraction)
			console.log("🔍 Trying title extraction patterns on text:", text.substring(0, 200));
			for (const pattern of fieldPatterns.title) {
				const match = text.match(pattern);
				console.log("🔍 Pattern:", pattern.toString(), "Match:", match ? match[1] : "no match");
				if (match && match[1]) {
					extractedFields.title = match[1].trim();
					console.log("✅ Title extracted via pattern:", extractedFields.title);
					break;
				}
			}

			// Fallback: use first substantial non-metadata line
			if (!extractedFields.title) {
				// First, try to find the title in the original text using word patterns
				// Look for patterns like "Document Title för anmälan avseende..." after metadata
				// More flexible pattern to match the actual text structure
				const titleMatch = text.match(/STYRDOKUMENT\s+\d+\s+Dnr\s+[A-Z0-9/\s]+\s+GÖTEBORGS\s+UNIVERSITET\s+([^Publicerad]+?)(?:\s+Publicerad|$)/i);
				
				if (titleMatch) {
					let title = titleMatch[1].trim();
					// Clean up any remaining metadata
					title = title.replace(/\s+(Beslutsfattare|Ansvarig\s+enhet|Beslutsdatum|Giltighetstid).*$/i, "").trim();
					if (title.length > 10 && title.length < 200) {
						extractedFields.title = title;
					}
				}
				
				// If still no title, look for title in the first few lines but skip obvious metadata
				if (!extractedFields.title) {
					for (let i = 0; i < Math.min(8, lines.length); i++) {
						const line = lines[i];
						
						// Skip metadata lines entirely
						if (/^(STYRDOKUMENT|Dnr|GU|\d{4}[-/]\d+|Sida\s+\d+|Publicerad|https?:|Beslut|Rektor|GÖTEBORGS\s+UNIVERSITET)/.test(line)) {
							continue;
						}
						
						// Look for meaningful title content - allow longer titles
						if (line.length > 20 && line.length < 300 && 
							!line.includes("@") && !line.includes("http") &&
							!/^\d+$/.test(line.trim())) {
							
							// Clean the title by removing any metadata suffixes
							let cleanTitle = line
								.replace(/\s+Publicerad.*$/i, "")
								.replace(/\s+https?:.*$/i, "")
								.replace(/\s+Dnr.*$/i, "")
								.replace(/\s+GU.*$/i, "")
								.trim();
							
							if (cleanTitle.length > 10) {
								extractedFields.title = cleanTitle;
								break;
							}
						}
					}
				}
				
				// Final fallback: try to piece together multi-line titles
				if (!extractedFields.title) {
					const potentialTitleLines = [];
					for (let i = 0; i < Math.min(8, lines.length); i++) {
						const line = lines[i];
						if (!/^(STYRDOKUMENT|Dnr|GU|\d{4}[-/]\d+|Publicerad|https?:|Beslut|Rektor|GÖTEBORGS)/.test(line) &&
							line.length > 5 && line.length < 200) {
							potentialTitleLines.push(line);
						}
					}
					
					if (potentialTitleLines.length > 0) {
						// Join first few lines that might form the title - allow longer titles
						const combinedTitle = potentialTitleLines.slice(0, 4).join(" ").trim();
						if (combinedTitle.length > 10 && combinedTitle.length < 400) {
							extractedFields.title = combinedTitle;
						}
					}
				}
			}

			// Extract responsible unit
			for (const pattern of fieldPatterns.unit) {
				const match = text.match(pattern);
				if (match && match[1]) {
					extractedFields.unit = match[1].trim();
					break;
				}
			}

			// Helper function to normalize Swedish date formats
			const normalizeDateFormat = (dateStr) => {
				if (!dateStr) return "";
				
				// Already in YYYY-MM-DD format
				if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
					return dateStr;
				}
				
				// Convert DD/MM/YYYY or DD.MM.YYYY or DD-MM-YYYY to YYYY-MM-DD
				const match = dateStr.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
				if (match) {
					const day = match[1].padStart(2, "0");
					const month = match[2].padStart(2, "0");
					const year = match[3];
					return `${year}-${month}-${day}`;
				}
				
				return dateStr; // Return as-is if can't parse
			};

			// Extract decision date
			for (const pattern of fieldPatterns.decisionDate) {
				const match = text.match(pattern);
				if (match && match[1]) {
					extractedFields.decisionDate = normalizeDateFormat(match[1].trim());
					break;
				}
			}

			// Extract validity period and split into from/to dates
			for (const pattern of fieldPatterns.validityPeriod) {
				const match = text.match(pattern);
				if (match && match[1]) {
					const validityText = match[1].trim().toLowerCase();
					
					if (validityText.includes("tillsvidare") || validityText.includes("tills vidare")) {
						extractedFields.validFrom = extractedFields.decisionDate || new Date().toISOString().split("T")[0];
						extractedFields.validTo = ""; // Indefinite
					} else {
						// Look for date ranges with various formats
						const dateRangeMatch = validityText.match(/(\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4})\s*[-–]\s*(\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4})/);
						if (dateRangeMatch) {
							extractedFields.validFrom = normalizeDateFormat(dateRangeMatch[1]);
							extractedFields.validTo = normalizeDateFormat(dateRangeMatch[2]);
						} else {
							// Single date, assume it's the start date
							const singleDateMatch = validityText.match(/(\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4})/);
							if (singleDateMatch) {
								extractedFields.validFrom = normalizeDateFormat(singleDateMatch[1]);
							}
						}
					}
					break;
				}
			}

			// Extract description/summary
			for (const pattern of fieldPatterns.description) {
				const match = text.match(pattern);
				if (match && match[1]) {
					extractedFields.description = match[1].trim().substring(0, 300);
					break;
				}
			}

			// Fallback description - find first substantial paragraph
			if (!extractedFields.description) {
				for (const line of lines) {
					if (line.length > 40 && 
						!/^(STYRDOKUMENT|Dnr|GU|[A-ZÅÄÖ][a-zåäö]*:|Sida\s+\d+)/.test(line) &&
						!line.includes("@") && !line.includes("http")) {
						extractedFields.description = line.substring(0, 300);
						break;
					}
				}
			}

			// Extract reference number
			for (const pattern of fieldPatterns.referenceNumber) {
				const match = text.match(pattern);
				if (match && match[1]) {
					extractedFields.referenceNumber = match[1].trim();
					break;
				}
			}

			// Extract keywords based on Swedish administrative terms
			const swedishKeywords = [
				"disciplin", "disciplinär", "förseelse", "misstanke",
				"policy", "riktlinjer", "styrdokument", 
				"handläggning", "handläggningsordning",
				"student", "doktorand", "anställd",
				"beslut", "fastställande", "godkännande",
				"utbildning", "forskning", "administration"
			];
			
			extractedFields.keywords = swedishKeywords.filter(keyword => 
				fullText.includes(keyword)
			);

			// Calculate confidence based on successful field extractions
			const fieldsFound = [
				extractedFields.title,
				extractedFields.unit,
				extractedFields.decisionDate || extractedFields.validFrom,
				extractedFields.description
			].filter(field => field && field.length > 0).length;

			extractedFields.confidence = fieldsFound / 4; // 0-1 scale

			return extractedFields;
		  };

		  // Fallback: Basic content extraction for non-Swedish documents
		  const extractDecisionInfo = (text) => {
			const lowerText = text.toLowerCase();
			
			// Try to extract matter/topic
			const matterKeywords = ["budget", "policy", "hiring", "project", "approval", "contract", "decision", "resolution"];
			const detectedMatter = matterKeywords.find(keyword => lowerText.includes(keyword)) || "";

			// Extract potential keywords (words longer than 3 chars, appearing more than once)
			const words = text.match(/\b[a-zA-Z]{4,}\b/g) || [];
			const wordCount = {};
			words.forEach(word => {
			  const w = word.toLowerCase();
			  wordCount[w] = (wordCount[w] || 0) + 1;
			});
			
			const keywords = Object.entries(wordCount)
			  .filter(([word, count]) => count > 1 && !["that", "this", "with", "from", "they", "will", "have", "been"].includes(word))
			  .sort(([,a], [,b]) => b - a)
			  .slice(0, 5)
			  .map(([word]) => word)
			  .join(", ");

			return { matter: detectedMatter, keywords };
		  };

		  // Perform intelligent document analysis with error handling
		  let swedishFields = null;
		  try {
			swedishFields = extractSwedishDocumentFields(extractedText, extractedMetadata.keyValuePairs);
			console.log("=== FINAL SWEDISH FIELDS EXTRACTION ===");
			console.log("Raw extracted text (first 300 chars):", extractedText.substring(0, 300));
			console.log("Swedish fields result:", swedishFields);
			console.log("Field extraction confidence:", swedishFields.confidence);
			console.log("==========================================");
		  } catch (error) {
			console.warn("Swedish document analysis failed, using fallback:", error);
			swedishFields = {
				title: "", unit: "", decisionDate: "", validFrom: "", validTo: "",
				description: "", referenceNumber: "", keywords: [], confidence: 0
			};
		  }
		  
		  const { matter: fallbackMatter, keywords: fallbackKeywords } = extractDecisionInfo(extractedText);

		  // Extract temp file IDs - use window.tempFileId if selectedFiles is empty (due to React state timing)
		  let extractedTempFileIds = selectedFiles.filter(f => f.tempFileId).map(f => f.tempFileId);
		  
		  // Fallback to window.tempFileId if selectedFiles is empty
		  if (extractedTempFileIds.length === 0 && window.tempFileId) {
		    extractedTempFileIds = [window.tempFileId];
		  }
		  
		  console.log("🔍 DocumentUpload: Extracting temp file IDs from selectedFiles:", extractedTempFileIds);
		  console.log("🔍 DocumentUpload: selectedFiles structure:", selectedFiles.map(f => ({ name: f.name, tempFileId: f.tempFileId })));
		  console.log("🔍 DocumentUpload: window.tempFileId as fallback:", window.tempFileId);

		  // Build comprehensive decision data with ONLY extracted fields (NO FALLBACKS)
		  const decisionData = {
			// Use Swedish extraction results ONLY (no fallbacks)
			title: swedishFields.title || "",
			matter: fallbackMatter || "",
			description: swedishFields.description || extractedText.slice(0, 500) || "",
			keywords: swedishFields.keywords.length > 0 ? swedishFields.keywords.join(", ") : (fallbackKeywords || ""),
			date: swedishFields.decisionDate || "",
			
			// Additional Swedish document fields for enhanced form prefilling
			unit: swedishFields.unit || "",
			validFrom: swedishFields.validFrom || "",
			validTo: swedishFields.validTo || "",
			validityPeriod: swedishFields.validityPeriod || "",
			referenceNumber: swedishFields.referenceNumber || "",
			confidence: swedishFields.confidence || 0,
			
			// Decision-specific fields (Sammanfattning should go to decisionDescription)
			decisionDescription: swedishFields.description || "",
			basisDescription: extractedText.slice(0, 1000) || "",

			summary: "Document processed using Textract",
			metadata: {
				sourceFiles: selectedFiles.map(f => ({
				  name: f.name,
				  size: f.size,
				  type: f.type,
				  processingMethod: method
				})),
				tempFileIds: extractedTempFileIds, // Use the extracted temp file IDs
				extractionMetadata: {
				  method: method,
				  textLength: extractedText.length,
				  tablesFound: extractedMetadata.tables.length,
				  formsFound: extractedMetadata.forms.length,
				  processedAt: new Date().toISOString()
				}
			}
		  };
	  
		  // Update processing status with extraction results
		  setProcessingStatus(`Extracted ${extractedText.length} characters, ${extractedMetadata.tables.length} tables, ${extractedMetadata.forms.length} forms`);

		  console.log("=== FINAL DECISION DATA BUILDING ===");
		  console.log("Title from Swedish fields:", swedishFields.title);
		  console.log("Unit from Swedish fields:", swedishFields.unit);
		  console.log("Reference number from Swedish fields:", swedishFields.referenceNumber);
		  console.log("Decision date from Swedish fields:", swedishFields.decisionDate);
		  console.log("Description from Swedish fields:", swedishFields.description);
		  console.log("=====================================");

		  // Use the new PrefillDecisionManager for better data management
		  try {
			setProcessingStatus("Saving extracted data...");
			// Import the manager dynamically (since we're in a .js file)
			const { PrefillDecisionManager } = await import("../../utils/PrefillDecisionManager");
			
			console.log("🔍 DocumentUpload: About to initialize from document with data:", {
			  title: decisionData.title,
			  matter: decisionData.matter,
			  description: decisionData.description,
			  unit: decisionData.unit,
			  date: decisionData.date,
			  referenceNumber: decisionData.referenceNumber
			});
			
			// Initialize proper prefill data structure with ALL Swedish extraction results
			const prefillData = PrefillDecisionManager.initializeFromDocument(primaryFile.file, {
			  title: decisionData.title,
			  matter: decisionData.matter,
			  description: decisionData.description,
			  text: extractedText,
			  summary: decisionData.summary,
			  keywords: decisionData.keywords,
			  date: decisionData.date,
			  
			  // Swedish document-specific fields
			  unit: decisionData.unit,
			  validFrom: decisionData.validFrom,
			  validTo: decisionData.validTo,
			  validityPeriod: decisionData.validityPeriod,
			  referenceNumber: decisionData.referenceNumber,
			  confidence: decisionData.confidence,
			  
			  // Decision-specific fields for form prefilling
			  decisionDescription: decisionData.decisionDescription,
			  basisDescription: decisionData.basisDescription,
			  
			  // Processing metadata
			  method: method,
			  textBlocks: extractedMetadata.textBlocks,
			  tables: extractedMetadata.tables,
			  forms: extractedMetadata.forms
			});
			
			console.log("🔍 DocumentUpload: Prefill data created:", prefillData);
			
			// Save using the manager
			PrefillDecisionManager.save(prefillData);
			
			console.log("🔍 DocumentUpload: Data saved to sessionStorage");
			console.log("🔍 DocumentUpload: Verification - sessionStorage contains:", sessionStorage.getItem("prefillDecisionData"));
			
			// Store the original file for attachment purposes
			try {
				const fileData = {
					name: primaryFile.file.name,
					size: primaryFile.file.size,
					type: primaryFile.file.type,
					lastModified: primaryFile.file.lastModified
				};
				
				console.log("🔍 DocumentUpload: Saving temp file ID for attachment:", fileData);
				console.log("🔍 DocumentUpload: primaryFile.tempFileId:", primaryFile.tempFileId);
				console.log("🔍 DocumentUpload: primaryFile.tempFileData:", primaryFile.tempFileData);
				
				// Store the temp file data including backend ID for attachment
				const tempFileData = {
					...fileData,
					tempFileId: primaryFile.tempFileId,
					tempFileData: primaryFile.tempFileData
				};
				sessionStorage.setItem("uploadedFileAttachment", JSON.stringify(tempFileData));
				
				// Store temp file ID instead of file object (no longer needed)
				window.tempFileId = primaryFile.tempFileId;
				window.tempFileData = primaryFile.tempFileData;
				
				console.log("🔍 DocumentUpload: tempFileId set:", window.tempFileId);
				console.log("🔍 DocumentUpload: tempFileData set:", !!window.tempFileData);
			} catch (fileError) {
				console.warn("Could not save file for attachment:", fileError);
			}
			setProcessingStatus("Data saved successfully");
			
		  } catch (importError) {
			// Fallback to original method
			sessionStorage.setItem("prefillDecisionData", JSON.stringify(decisionData));
			setProcessingStatus("Data saved (fallback method)");
		  }
		  
		  sessionStorage.removeItem("documentToastShown");
		  
		  setProcessingStatus("Processing complete!");

		  // Don't show toast here - it will be shown in AddDecisionPage
		  // toast.success("Document processed successfully!", { autoClose: 3000 });
	  
		  // Navigate to decision form
		  handleClose();
		  console.log("🔍 DocumentUpload: About to navigate, tempFileId exists:", !!window.tempFileId);
		  navigate("/add-decision");
	  
		} catch (error) {
		  
		  // Determine error type and show appropriate toast
		  let errorTitle = "Textract Processing Failed";
		  let errorMessage = "Unable to process document with AWS Textract. ";
		  
		  if (error.message?.includes("too large")) {
			errorMessage = "File is too large. Please use a file smaller than 5MB.";
			errorTitle = "File Too Large";
		  } else if (error.message?.includes("empty")) {
			errorMessage = "File appears to be empty or corrupted.";
			errorTitle = "Invalid File";
		  } else if (error.name === "AccessDeniedException") {
			errorMessage = "AWS Textract access denied. Please check your permissions and try again.";
			errorTitle = "Access Denied";
		  } else if (error.name === "InvalidParameterException") {
			errorMessage = "File format not supported by AWS Textract or file is corrupted.";
			errorTitle = "Invalid File Format";
		  } else if (error.name === "UnsupportedDocumentException") {
			errorMessage = "This PDF format is not supported by AWS Textract. Common causes: password protection, encryption, or scanned images. Try: 1) Remove password protection, 2) Use a different PDF, or 3) Convert to PNG/JPEG.";
			errorTitle = "Unsupported PDF Format";
		  } else if (error.name === "ThrottlingException") {
			errorMessage = "AWS Textract is currently busy. Please wait a moment and try again.";
			errorTitle = "Service Busy";
		  } else if (error.name === "LimitExceededException") {
			errorMessage = "AWS Textract usage limit exceeded. Please try again later.";
			errorTitle = "Usage Limit Exceeded";
		  } else if (error.message?.includes("Network") || error.name === "NetworkingError") {
			errorMessage = "Network connection failed. Please check your internet connection and try again.";
			errorTitle = "Connection Error";
		  } else {
			errorMessage = `Textract processing failed: ${error.message}`;
			errorTitle = "Processing Error";
		  }
		  
		  // Show error toast and stop processing
		  setProcessingStatus(`${errorTitle}`);
		  toast.error(`${errorTitle}: ${errorMessage}`, { 
			autoClose: 10000,
			closeOnClick: true 
		  });
		  
		  console.log("� NO FALLBACKS - Processing stopped due to Textract failure");
		  
		  // Show error in UI and reset to upload step
		  setErrors([`${errorTitle}: ${errorMessage}`]);
		  setCurrentStep(UPLOAD_STEPS.SELECT);
		  setProcessingStatus("");
		}
	  
		setIsProcessing(false);
	  }, [selectedFiles, navigate, handleClose]);

	const handleConversionComplete = useCallback((decisionData) => {
		// This will be called when the user creates a decision from the document
		onDecisionCreated?.(decisionData);
		handleClose();
	}, [onDecisionCreated, handleClose]);

	const getStepTitle = () => {
		switch (currentStep) {
			case UPLOAD_STEPS.SELECT:
				return "Upload Decision Document";
			case UPLOAD_STEPS.PROCESSING:
				return "Processing Document";
			default:
				return "Upload Document";
		}
	};

	const getStepDescription = () => {
		switch (currentStep) {
			case UPLOAD_STEPS.SELECT:
				return "Drag & drop or select your document. Supports PDF, PNG, and JPEG files.";
			case UPLOAD_STEPS.PROCESSING:
				return "Using AI to extract decision information from your document";
			default:
				return "Upload your document";
		}
	};

	const getStepNumber = () => {
		switch (currentStep) {
			case UPLOAD_STEPS.SELECT:
				return 1;
			case UPLOAD_STEPS.PROCESSING:
				return 2;
			default:
				return 1;
		}
	};

	// Concise subtitles for the modal header
	const getStepSubtitle = () => {
		switch (currentStep) {
			case UPLOAD_STEPS.SELECT:
				return "Max 5MB • PDF, PNG, JPEG";
			case UPLOAD_STEPS.PROCESSING:
				return "AI extraction in progress";
			default:
				return "Choose a document to get started";
		}
	};

	const renderStepContent = () => {
		switch (currentStep) {
			case UPLOAD_STEPS.SELECT:
				return (
					<div style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: "3rem 2rem 4rem",
						minHeight: "400px",
						textAlign: "center"
					}}>
						<DocumentDropzone 
							onFilesSelected={handleFilesSelected}
							selectedFiles={selectedFiles}
							onFileRemove={handleFileRemove}
							errors={errors}
							uploadProgress={uploadProgress}
						/>

					</div>
				);
			case UPLOAD_STEPS.PROCESSING:
				return (
					<div style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						justifyContent: "center",
						padding: "3rem 2rem 4rem",
						minHeight: "400px",
						textAlign: "center"
					}}>
						{/* Enhanced Loading Spinner */}
						<div style={{
							position: "relative",
							width: "80px",
							height: "80px",
							marginBottom: "2.5rem"
						}}>
							<div style={{
								width: "80px",
								height: "80px",
								border: "4px solid #DDE5EF",
								borderTop: "4px solid #33AF89",
								borderRadius: "50%",
								animation: "spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite",
							}} />
							<div style={{
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
								width: "32px",
								height: "32px",
								background: "linear-gradient(135deg, #33AF89 0%, #2d9a78 100%)",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}>
								<img src={PdfIcon} alt="Document" style={{ width: "16px", height: "16px", filter: "brightness(0) invert(1)" }} />
							</div>
						</div>
						
						<h4 style={{
							fontSize: "2rem",
							fontWeight: "700",
							fontFamily: "museo, serif",
							color: "#0F0F0F",
							marginBottom: "1.5rem",
							margin: "0 0 1.5rem 0"
						}}>
							Processing Document
						</h4>
						
						<p style={{
							fontSize: "1.25rem",
							color: "#4D6382",
							lineHeight: "1.6",
							maxWidth: "480px",
							marginBottom: "1rem",
							margin: "0 0 1rem 0",
							fontFamily: "museo, serif"
						}}>
							Extracting decision information from your document using AWS Textract.
						</p>
						
						{/* Dynamic Processing Status */}
						{processingStatus && (
							<div style={{
								background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
								border: "1px solid #dee2e6",
								borderRadius: "8px",
								padding: "0.75rem 1rem",
								marginBottom: "1.5rem",
								fontSize: "0.95rem",
								color: "#495057",
								fontFamily: "museo, serif",
								fontWeight: "500",
								textAlign: "center",
								maxWidth: "400px"
							}}>
								{processingStatus}
							</div>
						)}
						
						<p style={{
							fontSize: "0.9rem",
							color: "#9ca3af",
							lineHeight: "1.4",
							maxWidth: "300px",
							margin: "0",
							fontFamily: "museo, serif"
						}}>
							Extracting decision data...
						</p>
						
						<style>{`
							@keyframes spin {
								0% { transform: rotate(0deg); }
								100% { transform: rotate(360deg); }
							}
							
							@keyframes shimmer {
								0% { transform: translateX(-100%); }
								100% { transform: translateX(100%); }
							}
						`}</style>
					</div>
				);
			default:
				return null;
		}
	};

	// No actions needed - everything is automatic
	const renderActions = () => {
		return null;
	};

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleClose}
			style={StyledTallModal}
			contentLabel={getStepTitle()}
			ariaHideApp={false}
		>
			{/* Modal Header - Match App Design */}
			<StyledModalHeader>
				<div style={{
					display: "flex",
					alignItems: "center",
					gap: "1rem",
					margin: 0
				}}>
					<div style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: "3rem",
						height: "3rem",
						background: "linear-gradient(135deg, #33AF89 0%, #2d9a78 100%)",
						borderRadius: "12px",
						boxShadow: "0 4px 12px rgba(51, 175, 137, 0.25)"
					}}>
						<img src={PdfIcon} alt="Document Upload" style={{ 
							width: "1.5rem", 
							height: "1.5rem",
							filter: "brightness(0) invert(1)"
						}} />
					</div>
					<div>
						<h2 style={{
							margin: 0,
							fontSize: "1.75rem",
							fontWeight: "700",
							fontFamily: "museo, serif",
							color: "#0F0F0F",
							lineHeight: "1.2"
						}}>
							{getStepTitle()}
						</h2>
						<p style={{
							margin: 0,
							fontSize: "1rem",
							color: "#4D6382",
							fontFamily: "museo, serif"
						}}>
							{getStepSubtitle()}
						</p>
					</div>
				</div>
				<button
					onClick={handleClose}
					style={{
						background: "none",
						border: "none",
						cursor: "pointer",
						padding: "0.75rem",
						borderRadius: "8px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						transition: "all 0.2s ease",
						width: "3rem",
						height: "3rem",
						color: "#4D6382"
					}}
					onMouseEnter={(e) => {
						e.target.style.backgroundColor = "#F0F4F8";
						e.target.style.color = "#33AF89";
					}}
					onMouseLeave={(e) => {
						e.target.style.backgroundColor = "transparent";
						e.target.style.color = "#4D6382";
					}}
				>
					<span style={{ fontSize: "1.5rem", fontWeight: "400" }}>×</span>
				</button>
			</StyledModalHeader>

			<Container>
				{/* Content */}
				<Row>
					<Col>
						{renderStepContent()}
					</Col>
				</Row>
			</Container>
		</Modal>
	);
}

DocumentUploadModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onDecisionCreated: PropTypes.func,
};